import React, { useCallback, useEffect, useState } from 'react';
import { Check, X } from 'lucide-react';
import teamsService from '../services/teamsService';
import TeamAvatar from './ui/TeamAvatar';

/**
 * Team invitations waiting for the signed-in user.
 *
 * There WAS a way to accept one — the notification created alongside the
 * invitation carries the token, and the notifications panel can act on it. The
 * problem is that it was the *only* way, and it is not durable:
 *
 *   * `Notifications.cleanup_old_notifications/1` prunes notifications by age.
 *     An invitation lasts 7 days; if the prune runs first, the sole route to
 *     that token is gone and the invitation becomes unacceptable while still
 *     showing as pending to the admin who sent it.
 *   * dismissing the notification has the same effect, and dismissing a
 *     notification is not something anyone expects to be irreversible.
 *   * the email's "Accept invitation" button pointed at a page the marketing
 *     site does not serve, so it answered 200 with the homepage — no error,
 *     just nothing.
 *
 * This is the durable route: derived from the invitation rows themselves, so
 * it is correct for as long as the invitation is, and unaffected by anything
 * that happens to notifications.
 *
 * A banner above the teams list rather than a page of its own — it is empty
 * for almost everyone almost always, and something that renders nothing most
 * of the time should not also be somewhere you have to navigate to.
 */
export default function TeamPendingInvitations({ onAccepted }) {
  const [rows, setRows] = useState([]);
  const [busyToken, setBusyToken] = useState(null);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setRows(await teamsService.getPendingInvitations());
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const next = await teamsService.getPendingInvitations();
      if (!cancelled) setRows(next);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const act = async (token, accept) => {
    setBusyToken(token);
    setError('');
    try {
      if (accept) {
        await teamsService.acceptInvitation(token);
        await onAccepted?.();
      } else {
        await teamsService.declineInvitation(token);
      }
      await load();
    } catch (e) {
      setError(
        e?.response?.data?.message ||
          (accept ? 'Could not accept that invitation.' : 'Could not decline that invitation.')
      );
      // Re-read either way: "already a member" and "expired" both mean the row
      // has moved on, and leaving the stale offer on screen invites a second
      // click that fails the same way.
      await load();
    } finally {
      setBusyToken(null);
    }
  };

  if (rows.length === 0) return null;

  return (
    <div className="team-invites">
      {rows.map((inv) => (
        <div key={inv.token} className="team-invite-row">
          <TeamAvatar team={inv.team} size={28} />
          <div className="team-invite-texts">
            <span className="team-invite-name">
              You've been invited to <strong>{inv.team.name}</strong>
            </span>
            <span className="team-invite-sub">
              as {inv.role}
              {inv.expires_at &&
                ` · expires ${new Date(inv.expires_at).toLocaleDateString('en-GB')}`}
            </span>
          </div>
          <button
            type="button"
            className="org-btn org-btn--primary"
            disabled={busyToken === inv.token}
            onClick={() => act(inv.token, true)}
          >
            <Check size={13} aria-hidden /> Accept
          </button>
          <button
            type="button"
            className="org-btn"
            disabled={busyToken === inv.token}
            onClick={() => act(inv.token, false)}
          >
            <X size={13} aria-hidden /> Decline
          </button>
        </div>
      ))}
      {error && <p className="org-error">{error}</p>}
    </div>
  );
}
